import {OtsDateTime} from "../components/OtsDateTime";
import {PencilSquareIcon} from "@heroicons/react/20/solid";
import type {TicketCostInput, TicketCostInputValue} from "./ticket-cost-types";

export type TicketCostInputListProps = {
  inputs: TicketCostInput[];
  onEdit?: () => void;
};

const hasValue = (value?: TicketCostInputValue) => value !== undefined && (
  value.numberValue !== undefined
  || value.booleanValue !== undefined
  || value.temporalValue !== undefined
  || value.stringValue !== undefined
);

const formatValue = ({definition, value}: TicketCostInput) => {
  if (!hasValue(value)) return "Not set";
  if (definition.valueType === "NUMBER") return `${new Intl.NumberFormat("en-US", {maximumFractionDigits: 3}).format(value?.numberValue ?? 0)}${definition.unitOfMeasure ? ` ${definition.unitOfMeasure}` : ""}`;
  if (definition.valueType === "BOOLEAN") return value?.booleanValue ? "Yes" : "No";
  if (definition.valueType === "TEMPORAL") return <OtsDateTime value={value?.temporalValue} empty="Not set" />;
  return value?.stringValue ?? "Not set";
};

/** Definition-driven cost inputs, including unset definitions and override provenance. */
export const TicketCostInputList = ({inputs, onEdit}: TicketCostInputListProps) => (
  <div>
    <div className="divide-y divide-[var(--ots-outline-variant)]">
      {inputs.map((input) => {
        const populated = hasValue(input.value);
        return (
          <div key={input.definition.id} className="grid gap-x-4 gap-y-1 py-2.5 text-xs sm:grid-cols-[minmax(0,1fr)_auto]">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-semibold text-[var(--ots-text)]">{input.definition.name}</span>
                <span className="font-mono text-[10px] text-[var(--ots-text-muted)]">{input.definition.inputKey}</span>
              </div>
              <p className="mt-0.5 text-[10px] text-[var(--ots-text-muted)]">
                {input.definition.valueType}
                {input.definition.unitOfMeasureType && <> · {input.definition.unitOfMeasureType}</>}
              </p>
            </div>
            <div className="text-left sm:min-w-40 sm:text-right">
              <strong className={populated ? "tabular-nums text-[var(--ots-secondary)]" : "font-normal text-[var(--ots-text-muted)]"}>{formatValue(input)}</strong>
              {populated && <p className="mt-0.5 text-[10px] text-[var(--ots-text-muted)]">{input.value?.sourceType === "USER" ? "User supplied" : "System supplied"}</p>}
            </div>
            {input.value?.reason && <p className="text-[11px] text-[var(--ots-text-muted)] sm:col-span-2"><span className="font-medium text-[var(--ots-text)]">Reason:</span> {input.value.reason}</p>}
          </div>
        );
      })}
      {!inputs.length && <p className="py-3 text-xs text-[var(--ots-text-muted)]">No cost input definitions are available.</p>}
    </div>
    {onEdit && (
      <button type="button" onClick={onEdit} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ots-primary)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]">
        <PencilSquareIcon className="size-3.5"/> Edit cost inputs
      </button>
    )}
  </div>
);
